
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, Check, Code, FileCode, FileQuestion, Info, Server, Zap } from 'lucide-react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * Developer documentation component for the Accesstopia widget.
 * Provides comprehensive guides, API references, and troubleshooting help.
 */
const DeveloperDocs = () => {
  const [expandedExample, setExpandedExample] = useState<string | null>(null);

  const toggleExample = (id: string) => {
    setExpandedExample(expandedExample === id ? null : id);
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="integration" className="w-full">
        <TabsList className="grid grid-cols-4 mb-4">
          <TabsTrigger value="integration">Integration Guide</TabsTrigger>
          <TabsTrigger value="api">API Reference</TabsTrigger>
          <TabsTrigger value="events">Events & Callbacks</TabsTrigger>
          <TabsTrigger value="troubleshooting">Troubleshooting</TabsTrigger>
        </TabsList>

        {/* Integration Guide */}
        <TabsContent value="integration" className="space-y-4">
          <h2 className="text-xl font-semibold">Integration Guide</h2>
          <p className="text-muted-foreground">
            Follow these steps to integrate the Accesstopia widget into your website.
          </p>

          <div className="p-4 bg-muted/50 rounded-lg space-y-4">
            <h3 className="font-medium flex items-center">
              <FileCode className="mr-2 h-4 w-4" /> Basic Implementation
            </h3>
            
            <p className="text-sm">
              Add the following script to your website's HTML file, right before the closing <code>&lt;/body&gt;</code> tag:
            </p>

            <SyntaxHighlighter
              language="html"
              style={tomorrow}
              customStyle={{
                fontSize: '0.85rem',
                borderRadius: '0.5rem',
                padding: '1rem',
              }}
            >
{`<!-- Accesstopia Widget -->
<script>
  window.ACCESSTOPIA_CONFIG = {
    buttonPosition: 'bottom-left',
    voiceAssistantPosition: 'bottom-center',
    defaultDarkMode: false,
    defaultFontSize: 100,
    primaryColor: '#0066cc',
    borderRadius: '0.5rem',
    enableVoiceAssistant: true
  };
</script>
<script type="text/javascript">
  (function() {
    var s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = 'https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/widget?key=YOUR_INSTALLATION_KEY&v=1.0.3';
    var x = document.getElementsByTagName('script')[0];
    x.parentNode.insertBefore(s, x);
  })();
</script>`}
            </SyntaxHighlighter>
            
            <p className="text-sm">
              Replace <code>YOUR_INSTALLATION_KEY</code> with your actual installation key from your dashboard.
            </p>
          </div>

          <div className="p-4 bg-muted/50 rounded-lg space-y-4">
            <h3 className="font-medium flex items-center">
              <Code className="mr-2 h-4 w-4" /> Framework Integration
            </h3>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="react">
                <AccordionTrigger>React Integration</AccordionTrigger>
                <AccordionContent>
                  <SyntaxHighlighter
                    language="jsx"
                    style={tomorrow}
                    customStyle={{
                      fontSize: '0.85rem',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                    }}
                  >
{`import { useEffect } from 'react';

function AccesstopiaIntegration({ installKey }) {
  useEffect(() => {
    // Set configuration
    window.ACCESSTOPIA_CONFIG = {
      buttonPosition: 'bottom-left',
      voiceAssistantPosition: 'bottom-center',
      defaultDarkMode: false,
      defaultFontSize: 100
    };
    
    // Load script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = \`https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/widget?key=\${installKey}&v=1.0.3\`;
    
    script.onload = () => console.log('Accesstopia widget loaded');
    script.onerror = (e) => console.error('Failed to load Accesstopia widget', e);
    
    document.body.appendChild(script);
    
    // Cleanup on unmount
    return () => {
      document.body.removeChild(script);
    };
  }, [installKey]);
  
  return null;
}

export default AccesstopiaIntegration;

// Usage in your app
// <AccesstopiaIntegration installKey="YOUR_INSTALLATION_KEY" />`}
                  </SyntaxHighlighter>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="next">
                <AccordionTrigger>Next.js Integration</AccordionTrigger>
                <AccordionContent>
                  <SyntaxHighlighter
                    language="jsx"
                    style={tomorrow}
                    customStyle={{
                      fontSize: '0.85rem',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                    }}
                  >
{`// components/AccesstopiaWidget.jsx
'use client';

import { useEffect } from 'react';

export default function AccesstopiaWidget({ installKey }) {
  useEffect(() => {
    // Set configuration
    window.ACCESSTOPIA_CONFIG = {
      buttonPosition: 'bottom-left',
      voiceAssistantPosition: 'bottom-center',
      defaultDarkMode: false,
      defaultFontSize: 100
    };
    
    // Load script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = \`https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/widget?key=\${installKey}&v=1.0.3\`;
    
    script.onload = () => console.log('Accesstopia widget loaded');
    script.onerror = (e) => console.error('Failed to load Accesstopia widget', e);
    
    document.body.appendChild(script);
    
    // Cleanup on unmount
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [installKey]);
  
  return null;
}

// In layout.js or your root component:
// <AccesstopiaWidget installKey="YOUR_INSTALLATION_KEY" />`}
                  </SyntaxHighlighter>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="vue">
                <AccordionTrigger>Vue.js Integration</AccordionTrigger>
                <AccordionContent>
                  <SyntaxHighlighter
                    language="html"
                    style={tomorrow}
                    customStyle={{
                      fontSize: '0.85rem',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                    }}
                  >
{`<!-- AccesstopiaWidget.vue -->
<template>
  <div></div>
</template>

<script>
export default {
  props: {
    installKey: {
      type: String,
      required: true
    }
  },
  
  mounted() {
    // Set configuration
    window.ACCESSTOPIA_CONFIG = {
      buttonPosition: 'bottom-left',
      voiceAssistantPosition: 'bottom-center',
      defaultDarkMode: false,
      defaultFontSize: 100
    };
    
    // Load script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = \`https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/widget?key=\${this.installKey}&v=1.0.3\`;
    
    script.onload = () => console.log('Accesstopia widget loaded');
    script.onerror = (e) => console.error('Failed to load Accesstopia widget', e);
    
    document.body.appendChild(script);
    this.widgetScript = script;
  },
  
  beforeUnmount() {
    // Cleanup
    if (this.widgetScript && document.body.contains(this.widgetScript)) {
      document.body.removeChild(this.widgetScript);
    }
  }
}
</script>

<!-- Usage in App.vue or main component -->
<!-- <AccesstopiaWidget install-key="YOUR_INSTALLATION_KEY" /> -->`}
                  </SyntaxHighlighter>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </TabsContent>

        {/* API Reference */}
        <TabsContent value="api" className="space-y-4">
          <h2 className="text-xl font-semibold">API Reference</h2>
          <p className="text-muted-foreground">
            Complete documentation of all configuration options and methods available in the Accesstopia widget.
          </p>

          <div className="p-4 bg-muted/50 rounded-lg space-y-4">
            <h3 className="font-medium">Configuration Options</h3>
            <p className="text-sm mb-4">
              Configure the widget behavior through the <code>window.ACCESSTOPIA_CONFIG</code> object.
            </p>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left py-2 font-semibold">Option</th>
                    <th className="text-left py-2 font-semibold">Type</th>
                    <th className="text-left py-2 font-semibold">Default</th>
                    <th className="text-left py-2 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-2"><code>buttonPosition</code></td>
                    <td className="py-2">string</td>
                    <td className="py-2"><code>'bottom-left'</code></td>
                    <td className="py-2">Position of the accessibility toggle button. Options: <code>'bottom-left'</code>, <code>'bottom-right'</code>, <code>'top-left'</code>, <code>'top-right'</code></td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>voiceAssistantPosition</code></td>
                    <td className="py-2">string</td>
                    <td className="py-2"><code>'bottom-center'</code></td>
                    <td className="py-2">Position of the voice assistant panel. Options: <code>'bottom-center'</code>, <code>'bottom-left'</code>, <code>'bottom-right'</code></td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>defaultDarkMode</code></td>
                    <td className="py-2">boolean</td>
                    <td className="py-2"><code>false</code></td>
                    <td className="py-2">Whether dark mode is enabled by default</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>defaultFontSize</code></td>
                    <td className="py-2">number</td>
                    <td className="py-2"><code>100</code></td>
                    <td className="py-2">Default font size percentage (100 = normal)</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>primaryColor</code></td>
                    <td className="py-2">string</td>
                    <td className="py-2"><code>'#0066cc'</code></td>
                    <td className="py-2">Primary color for the widget UI elements</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>borderRadius</code></td>
                    <td className="py-2">string</td>
                    <td className="py-2"><code>'0.5rem'</code></td>
                    <td className="py-2">Border radius for UI elements</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>fontFamily</code></td>
                    <td className="py-2">string</td>
                    <td className="py-2"><code>undefined</code></td>
                    <td className="py-2">Custom font family for the widget</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>enableVoiceAssistant</code></td>
                    <td className="py-2">boolean</td>
                    <td className="py-2"><code>true</code></td>
                    <td className="py-2">Enable or disable the voice assistant feature</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>language</code></td>
                    <td className="py-2">string</td>
                    <td className="py-2"><code>'en-US'</code></td>
                    <td className="py-2">Language for voice recognition (e.g., 'en-US', 'es-ES')</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>debug</code></td>
                    <td className="py-2">boolean</td>
                    <td className="py-2"><code>false</code></td>
                    <td className="py-2">Enable debug logging to console</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>routes</code></td>
                    <td className="py-2">object</td>
                    <td className="py-2"><code>{}</code></td>
                    <td className="py-2">Map of route names to paths for voice navigation</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>commands</code></td>
                    <td className="py-2">object</td>
                    <td className="py-2"><code>{}</code></td>
                    <td className="py-2">Custom voice commands mapping</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>customResponses</code></td>
                    <td className="py-2">object</td>
                    <td className="py-2"><code>{}</code></td>
                    <td className="py-2">Custom responses for different scenarios</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-4 bg-muted/50 rounded-lg space-y-4">
            <h3 className="font-medium">JavaScript API</h3>
            <p className="text-sm mb-4">
              The Accesstopia widget exposes a global API for programmatic control:
            </p>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left py-2 font-semibold">Method</th>
                    <th className="text-left py-2 font-semibold">Parameters</th>
                    <th className="text-left py-2 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-2"><code>window.Accesstopia.show()</code></td>
                    <td className="py-2">None</td>
                    <td className="py-2">Show the accessibility panel</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>window.Accesstopia.hide()</code></td>
                    <td className="py-2">None</td>
                    <td className="py-2">Hide the accessibility panel</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>window.Accesstopia.toggle()</code></td>
                    <td className="py-2">None</td>
                    <td className="py-2">Toggle the visibility of the accessibility panel</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>window.Accesstopia.startVoiceAssistant()</code></td>
                    <td className="py-2">None</td>
                    <td className="py-2">Activate the voice assistant</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>window.Accesstopia.stopVoiceAssistant()</code></td>
                    <td className="py-2">None</td>
                    <td className="py-2">Deactivate the voice assistant</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>window.Accesstopia.getState()</code></td>
                    <td className="py-2">None</td>
                    <td className="py-2">Get the current state of accessibility settings</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>window.Accesstopia.setState(settings)</code></td>
                    <td className="py-2"><code>settings: object</code></td>
                    <td className="py-2">Update accessibility settings programmatically</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Events & Callbacks */}
        <TabsContent value="events" className="space-y-4">
          <h2 className="text-xl font-semibold">Events & Callbacks</h2>
          <p className="text-muted-foreground">
            Listen for widget events and implement custom behaviors.
          </p>

          <div className="p-4 bg-muted/50 rounded-lg">
            <h3 className="font-medium mb-4">Event Listeners</h3>
            <SyntaxHighlighter
              language="javascript"
              style={tomorrow}
              customStyle={{
                fontSize: '0.85rem',
                borderRadius: '0.5rem',
                padding: '1rem',
              }}
            >
{`// Listen for initialization
document.addEventListener('accesstopia:initialized', function(e) {
  console.log('Accesstopia widget initialized!', e.detail);
});

// Listen for settings changes
document.addEventListener('accesstopia:settingsChanged', function(e) {
  console.log('Accessibility settings changed:', e.detail);
  
  // Example: Sync with your app's state management
  if (e.detail.darkMode !== undefined) {
    // Update your app's dark mode
    updateAppDarkMode(e.detail.darkMode);
  }
});

// Listen for voice commands
document.addEventListener('accesstopia:voiceCommand', function(e) {
  console.log('Voice command detected:', e.detail.command);
  console.log('Handled by widget:', e.detail.handled);
  
  // Handle custom commands if not handled by the widget
  if (!e.detail.handled) {
    handleCustomVoiceCommand(e.detail.command);
  }
});

// Listen for panel visibility changes
document.addEventListener('accesstopia:panelVisibilityChanged', function(e) {
  console.log('Panel visibility changed:', e.detail.visible);
});

// Listen for errors
document.addEventListener('accesstopia:error', function(e) {
  console.error('Accesstopia error:', e.detail);
});`}
            </SyntaxHighlighter>
          </div>

          <div className="p-4 bg-muted/50 rounded-lg">
            <h3 className="font-medium mb-4">Custom Event Handling Example</h3>
            <SyntaxHighlighter
              language="javascript"
              style={tomorrow}
              customStyle={{
                fontSize: '0.85rem',
                borderRadius: '0.5rem',
                padding: '1rem',
              }}
            >
{`// Example: Create a custom handler for voice commands
function setupAccesstopiaIntegration() {
  // Configuration override with callbacks
  window.ACCESSTOPIA_CONFIG = {
    buttonPosition: 'bottom-right',
    enableVoiceAssistant: true,
    
    // Add custom callbacks
    onInit: function(instance) {
      console.log('Accesstopia initialized with version:', instance.version);
    },
    
    onVoiceCommand: function(command, wasHandled) {
      // Only handle commands not processed by the widget
      if (!wasHandled && command.toLowerCase().includes('product')) {
        // Example: Navigate to product section when user says "show me products"
        if (command.toLowerCase().includes('show me product')) {
          document.querySelector('#products-section').scrollIntoView({ 
            behavior: 'smooth' 
          });
          return true; // Mark as handled
        }
      }
      return false; // Not handled by this callback
    },
    
    onStateChange: function(newState, oldState) {
      // Track changes for analytics
      if (newState.darkMode !== oldState.darkMode) {
        trackAnalyticsEvent('accessibility_setting_changed', {
          setting: 'darkMode',
          value: newState.darkMode
        });
      }
    }
  };
}

// Call this before widget script is loaded
setupAccesstopiaIntegration();`}
            </SyntaxHighlighter>
          </div>
        </TabsContent>

        {/* Troubleshooting */}
        <TabsContent value="troubleshooting" className="space-y-4">
          <h2 className="text-xl font-semibold">Troubleshooting</h2>
          <p className="text-muted-foreground">
            Common issues and solutions when integrating the Accesstopia widget.
          </p>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="widgetNotLoading">
              <AccordionTrigger className="font-medium">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                  Widget not loading or appearing
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-sm space-y-4">
                <p>If the widget doesn't appear on your site:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>Check that your installation key is valid and active in your dashboard</li>
                  <li>Verify that the script is being loaded after the DOM is ready (place before <code>&lt;/body&gt;</code>)</li>
                  <li>Check the browser console for any error messages</li>
                  <li>Ensure there are no Content Security Policy (CSP) restrictions blocking the script</li>
                  <li>Verify the domain matches the one registered with your installation key</li>
                </ol>

                <Alert className="mt-2">
                  <Info className="h-4 w-4" />
                  <AlertTitle>Debug Mode</AlertTitle>
                  <AlertDescription>
                    Enable debug mode by setting <code>debug: true</code> in your configuration to see detailed logs in the console.
                  </AlertDescription>
                </Alert>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="voiceAssistant">
              <AccordionTrigger className="font-medium">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                  Voice Assistant not working
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-sm space-y-4">
                <p>If the voice assistant isn't functioning correctly:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>Check if the browser supports the Web Speech API (Chrome, Edge, Safari)</li>
                  <li>Ensure microphone permissions are granted to the website</li>
                  <li>Verify <code>enableVoiceAssistant: true</code> in your configuration</li>
                  <li>Check if the user is using HTTPS (required for microphone access)</li>
                  <li>Try setting a specific language in the configuration that matches the user's speech</li>
                </ol>

                <Alert className="mt-2" variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Browser Support</AlertTitle>
                  <AlertDescription>
                    Voice recognition is not supported in Firefox and some mobile browsers. The widget will automatically hide voice features when unsupported.
                  </AlertDescription>
                </Alert>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="styling">
              <AccordionTrigger className="font-medium">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                  Styling conflicts with your website
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-sm space-y-4">
                <p>If the widget styling conflicts with your website styles:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>The widget uses shadow DOM to isolate its styles when possible</li>
                  <li>Customize the appearance with <code>primaryColor</code>, <code>borderRadius</code>, and <code>fontFamily</code> options</li>
                  <li>If conflicts persist, try adjusting z-index values in your CSS</li>
                  <li>Change the widget position to avoid overlapping with important elements</li>
                </ol>

                <div className="bg-muted p-3 rounded-md mt-2">
                  <h4 className="font-medium mb-2">Sample Custom Styling</h4>
                  <SyntaxHighlighter
                    language="javascript"
                    style={tomorrow}
                    customStyle={{
                      fontSize: '0.85rem',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                    }}
                  >
{`window.ACCESSTOPIA_CONFIG = {
  buttonPosition: 'top-right',
  primaryColor: '#8a2be2', // Custom purple
  borderRadius: '1rem',
  fontFamily: 'Roboto, sans-serif',
  customStyles: {
    button: {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    panel: {
      maxWidth: '400px'
    }
  }
};`}
                  </SyntaxHighlighter>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="performance">
              <AccordionTrigger className="font-medium">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                  Performance impact concerns
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-sm space-y-4">
                <p>To minimize performance impact:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>The widget is loaded asynchronously by default and won't block rendering</li>
                  <li>Voice assistant features are only activated when the user interacts with them</li>
                  <li>For high-traffic sites, consider lazy loading the widget on user interaction</li>
                </ol>

                <div className="bg-muted p-3 rounded-md mt-2">
                  <h4 className="font-medium mb-2">Optimized Loading Example</h4>
                  <SyntaxHighlighter
                    language="javascript"
                    style={tomorrow}
                    customStyle={{
                      fontSize: '0.85rem',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                    }}
                  >
{`// Load widget only after page is fully loaded
window.addEventListener('load', function() {
  // Delay loading by 2 seconds to prioritize critical content
  setTimeout(function() {
    var s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = 'https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/widget?key=YOUR_INSTALLATION_KEY&v=1.0.3';
    document.body.appendChild(s);
  }, 2000);
});`}
                  </SyntaxHighlighter>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="crossOrigin">
              <AccordionTrigger className="font-medium">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                  Cross-origin and security issues
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-sm space-y-4">
                <p>For cross-origin or security-related issues:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>The widget is designed to work across different domains with proper CORS headers</li>
                  <li>Check if your Content Security Policy (CSP) allows loading from <code>*.supabase.co</code></li>
                  <li>If using strict CSP, add the following to your policy:</li>
                </ol>

                <SyntaxHighlighter
                  language="html"
                  style={tomorrow}
                  customStyle={{
                    fontSize: '0.85rem',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                  }}
                >
{`<meta http-equiv="Content-Security-Policy" content="
  default-src 'self';
  script-src 'self' https://*.supabase.co;
  connect-src 'self' https://*.supabase.co;
  img-src 'self' data: https://*.supabase.co;
  style-src 'self' 'unsafe-inline';
  frame-src 'self';
">
`}
                </SyntaxHighlighter>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="p-4 bg-muted/50 rounded-lg space-y-3">
            <h3 className="font-medium flex items-center">
              <Zap className="mr-2 h-4 w-4" /> Common Error Codes
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left py-2 font-semibold">Error Code</th>
                    <th className="text-left py-2 font-semibold">Description</th>
                    <th className="text-left py-2 font-semibold">Solution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-2"><code>INVALID_KEY</code></td>
                    <td className="py-2">Invalid installation key</td>
                    <td className="py-2">Check your installation key in the dashboard</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>DOMAIN_MISMATCH</code></td>
                    <td className="py-2">Domain not authorized</td>
                    <td className="py-2">Add your domain in the dashboard settings</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>SUBSCRIPTION_EXPIRED</code></td>
                    <td className="py-2">Subscription inactive or expired</td>
                    <td className="py-2">Renew your subscription in the dashboard</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>API_ERROR</code></td>
                    <td className="py-2">Server-side API error</td>
                    <td className="py-2">Check server logs and contact support</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code>BROWSER_UNSUPPORTED</code></td>
                    <td className="py-2">Browser doesn't support required features</td>
                    <td className="py-2">Display a fallback UI for unsupported browsers</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between items-center p-4 bg-muted/50 rounded-lg">
            <div>
              <h3 className="font-medium flex items-center">
                <FileQuestion className="mr-2 h-4 w-4" /> Need More Help?
              </h3>
              <p className="text-sm text-muted-foreground">
                Contact our support team for personalized assistance.
              </p>
            </div>
            <div>
              <a href="https://accesstopia.com/support" target="_blank" rel="noreferrer" className="text-sm text-primary hover:text-primary/80 flex items-center">
                Contact Support <Server className="ml-1 h-3 w-3" />
              </a>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DeveloperDocs;
